import { useContext, useEffect } from "react";

import { FieldType } from "../../constants";
import { DataFormFieldContext } from "../../contexts/dataFormFieldContext";
import {
	FieldCheckbox,
	FieldDate,
	FieldDefaultText,
	FieldDropdown,
	FieldMultiCheckbox,
	FieldOfferingMultiCheckbox,
} from "../";
import { FieldAsyncDropdown } from "./fieldAsyncDropdown";
import { FieldDropdownMulti } from "./fieldDropdownMulti";
import { FieldFileAttachments } from "./fieldFileAttachments";
import { FieldJsonDict } from "./fieldJsonDict";
import { FieldRadioButtons } from "./fieldRadioButtons";
import { FieldTextArea } from "./fieldTextArea";

// TODO: move to utils
export const getDropdownValue = ({
	dropdownData,
	valueToFind,
	isMandatory,
}) => {
	if (valueToFind) {
		const selectedDropDown = dropdownData.filter(
			({ id, label }) => valueToFind === id || valueToFind === label,
		);

		if (selectedDropDown.length) {
			return selectedDropDown[0].id;
		}
	} else if (isMandatory && !!dropdownData[0]) {
		return dropdownData[0].id;
	}
	return valueToFind;
};

export const findFormType = (type) => {
	switch (type) {
		case FieldType.PASSWORD:
			return "password";
		case FieldType.NUMBER:
		case FieldType.POSITIVE_INTEGER:
			return "number";
		case FieldType.STRING:
		default:
			return "text";
	}
};

export const DataFormFieldFactory = ({
	errors = [{}],
	setFormData = () => console.log,
	currentFormData = {},
	fieldSetting,
}) => {
	fieldSetting.accessor = fieldSetting.accessor ?? ((data) => data[field]);
	fieldSetting.visible = fieldSetting.visible ?? true;
	fieldSetting.dropdownData = fieldSetting.dropdownData ?? [];
	const {
		field,
		isDisabledFn,
		dropdownData,
		visible,
		accessor,
		type,
		isMandatory,
		index,
		isAnIsDisabledTrigger = false,
	} = fieldSetting;

	const { triggerFn } = useContext(DataFormFieldContext);

	const fieldData = accessor(currentFormData);
	const setValue = (data) => {
		setFormData(field)(data);
		if (
			isAnIsDisabledTrigger ||
			type === FieldType.ATTACHMENTS ||
			type === FieldType.OFFERING_FEATURE_MULTI_CHECKBOX
		)
			triggerFn();
	};
	const error = errors[field];
	const { selectedRows } = currentFormData;
	const formSettingVariables = {
		fieldData,
		setValue,
		error,
		selectedRows,
		autoFocus: index === 0,
		...fieldSetting,
	};

	useEffect(() => {
		if (
			visible &&
			(type === FieldType.DROPDOWN ||
				type === FieldType.OFFERING_FEATURE_MULTI_CHECKBOX)
		) {
			const dropDownValue = getDropdownValue({
				dropdownData,
				valueToFind: fieldData,
				isMandatory,
			});
			setValue(dropDownValue);
		}
	}, []);

	if (isDisabledFn)
		formSettingVariables.isDisabled = isDisabledFn(currentFormData);

	if (visible === false) return null;

	switch (type) {
		case FieldType.ATTACHMENTS:
			return <FieldFileAttachments {...formSettingVariables} />;
		case FieldType.CHECKBOX:
			return (
				<FieldCheckbox
					data-testid="test-dataFormFieldFactory"
					{...formSettingVariables}
				/>
			);
		case FieldType.MULTI_CHECKBOX:
			return <FieldMultiCheckbox {...formSettingVariables} />;
		case FieldType.OFFERING_FEATURE_MULTI_CHECKBOX:
			return (
				<FieldOfferingMultiCheckbox
					setFeatures={setFormData("includedFeatures")}
					features={currentFormData?.includedFeatures}
					{...formSettingVariables}
				/>
			);
		case FieldType.DATE:
		case FieldType.DATE_EXPIRED:
			return <FieldDate {...formSettingVariables} />;
		case FieldType.DROPDOWN:
			return <FieldDropdown {...formSettingVariables} />;
		case FieldType.ASYNC_DROPDOWN:
			return <FieldAsyncDropdown {...formSettingVariables} />;
		case FieldType.MULTI_DROPDOWN:
			return <FieldDropdownMulti {...formSettingVariables} />;
		case FieldType.TEXT_AREA:
			return <FieldTextArea {...formSettingVariables} />;
		case FieldType.JSON_DICT:
			return <FieldJsonDict {...formSettingVariables} />;
		case FieldType.RADIO_BUTTONS:
			return <FieldRadioButtons {...formSettingVariables} />;
		case FieldType.PASSWORD:
		case FieldType.NUMBER:
		case FieldType.POSITIVE_INTEGER:
		case FieldType.STRING:
		default:
			formSettingVariables.formType = findFormType(type);
			return (
				<FieldDefaultText
					data-testid="test-dataFormFieldFactory"
					{...formSettingVariables}
				/>
			);
	}
};
